import { useContext, useMemo, useEffect } from 'react';
import { tiles } from '../../assets/tiles';

import {
  filterTilesByCategory,
  sortedTileCategories
} from '../../helpers/tilesHelper';
import { TranslationContext } from '../../handlers/TranslationsHandler';

import { ThemeEnum } from '../../handlers/ThemeHandler';
import { ThemeContext } from 'styled-components';
import { TilesContext } from '../../handlers/TilesHandler';
import { SiteContext } from '../../handlers/SiteHandler';
import { pdf } from '@react-pdf/renderer';
import { MemoizedMyDocument } from './Document';
import { localeDate } from '../../helpers/dateTime';
import { TilesData } from './interfaces';

export const useCatalogue = () => {
  const { translate, language } = useContext(TranslationContext);
  const { isInFinancesMode } = useContext(TilesContext);
  const { generateCatalogue, setGenerateCatalogue, setLoading } = useContext(
    SiteContext
  );
  const theme =
    ThemeEnum[(useContext(ThemeContext) as any)?.theme || ThemeEnum.light];

  const translatedTableOfContents = useMemo((): TilesData[] => {
    return sortedTileCategories(isInFinancesMode).map(category => {
      return {
        category: translate(category as string),
        tiles: filterTilesByCategory(category as string, translate).map(
          fct => ({
            id: fct.name,
            title: translate(fct.title) as string,
            isLarge: fct.isLarge,
            description: translate(fct.description),
            imageSrc: tiles[theme][language][fct.name]
          })
        )
      };
    });
  }, [isInFinancesMode, translate, theme, language]);

  useEffect(() => {
    if (generateCatalogue) {
      try {
        setLoading(true);

        pdf(
          <MemoizedMyDocument
            tilesData={translatedTableOfContents}
            translations={{
              tocTitle: translate('tocTitle'),
              themeTitle: `${translate(
                theme === 'dark' ? 'combinedDarkTheme' : 'combinedLightTheme'
              )}`,
              theme,
              language,
              catalogueIntroTitle: translate('catalogueIntroTitle'),
              catalogueIntroSubTitle: translate('catalogueIntroSubTitle'),
              catalogueIntroContent: translate('catalogueIntroContent'),
              catalogueIntroSubTitle2: translate('catalogueIntroSubTitle2'),
              catalogueIntroContent2: translate('catalogueIntroContent2'),
              version: `${translate('currentVersion')}: ${localeDate(
                language
              )}`,
              cockpit: translate('cockpit'),
              tiles: translate('tiles')
            }}
          />
        )
          .toBlob()
          .then(blob => {
            const url = URL.createObjectURL(blob);

            const downloadLink = document.createElement('a');
            downloadLink.href = url;
            downloadLink.download = `[ui!] COCKPIT ${translate(
              'tileCatalogue'
            )} - ${localeDate(language)}.pdf`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);

            window.open(url, '_blank');

            setGenerateCatalogue(false);
            setLoading(false);
          })
          .catch(e => {
            throw new Error(e);
          });
      } catch (e) {
        console.log('error generating pdf', e);
        setGenerateCatalogue(false);
        setLoading(false);
      }
    }
  }, [generateCatalogue]);
};
